import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  Search, Filter, RefreshCw, Eye, Edit, Phone, MapPin, 
  Package, Clock, CheckCircle, AlertCircle, XCircle,
  TrendingUp, Users, DollarSign, Truck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_COLORS = {
  QUOTE_GENERATED: 'bg-blue-100 text-blue-800',
  PICKUP_REQUESTED: 'bg-yellow-100 text-yellow-800',
  LEAD_CREATED: 'bg-gray-100 text-gray-800',
  PICKUP_ASSIGNED: 'bg-indigo-100 text-indigo-800',
  PICKUP_SCHEDULED: 'bg-purple-100 text-purple-800',
  PQ_IN_PROGRESS: 'bg-orange-100 text-orange-800',
  PQ_COMPLETED: 'bg-teal-100 text-teal-800',
  PRICE_NEGOTIATED: 'bg-cyan-100 text-cyan-800',
  PAYMENT_COMPLETED: 'bg-green-100 text-green-800',
  PO_GENERATED: 'bg-emerald-100 text-emerald-800',
  DEVICE_COLLECTED: 'bg-lime-100 text-lime-800',
  IN_TRANSIT: 'bg-sky-100 text-sky-800',
  RECEIVED_AT_TRC: 'bg-blue-100 text-blue-800',
  GRN_COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  REJECTED: 'bg-red-100 text-red-800',
  ON_HOLD: 'bg-amber-100 text-amber-800',
  PRICE_DISPUTED: 'bg-rose-100 text-rose-800'
};

const BuybackLeads = ({ user, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [leadSources, setLeadSources] = useState([]);

  useEffect(() => {
    fetchLeads();
    fetchDashboard();
    fetchLeadSources();
  }, [statusFilter, sourceFilter]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source_name', sourceFilter);
      
      const response = await axios.get(`${API}/api/buyback/leads?${params.toString()}`);
      setLeads(response.data.leads || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/api/buyback/dashboard`);
      setDashboard(response.data.dashboard);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await axios.get(`${API}/api/buyback/lead-sources`);
      setLeadSources(response.data.sources || []);
    } catch (err) {
      console.error('Error fetching lead sources:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLeads();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStatusIcon = (status) => {
    const icons = {
      QUOTE_GENERATED: <Clock className="w-4 h-4" />,
      PICKUP_REQUESTED: <Phone className="w-4 h-4" />,
      PICKUP_ASSIGNED: <Users className="w-4 h-4" />,
      PICKUP_SCHEDULED: <Clock className="w-4 h-4" />,
      PQ_COMPLETED: <CheckCircle className="w-4 h-4" />,
      PAYMENT_COMPLETED: <DollarSign className="w-4 h-4" />,
      DEVICE_COLLECTED: <Package className="w-4 h-4" />,
      IN_TRANSIT: <Truck className="w-4 h-4" />,
      GRN_COMPLETED: <CheckCircle className="w-4 h-4" />,
      CANCELLED: <XCircle className="w-4 h-4" />,
      REJECTED: <AlertCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Buyback Leads</h1>
          <p className="text-gray-500">Manage all buyback leads from multiple sources</p>
        </div>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-800">{dashboard.overview?.total_leads || 0}</p>
                  <p className="text-xs text-green-600">+{dashboard.overview?.today_leads || 0} today</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Value</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(dashboard.overview?.total_value)}</p>
                  <p className="text-xs text-gray-500">{dashboard.overview?.completed_count || 0} completed</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Pickups</p>
                  <p className="text-2xl font-bold text-gray-800">{dashboard.pending_actions?.pending_pickups || 0}</p>
                  <p className="text-xs text-yellow-600">Needs assignment</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Truck className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Value</p>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(dashboard.overview?.avg_value)}</p>
                  <p className="text-xs text-gray-500">Per lead</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow mb-6 p-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Lead ID, Name, Mobile, IMEI..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="QUOTE_GENERATED">Quote Generated</option>
                <option value="PICKUP_REQUESTED">Pickup Requested</option>
                <option value="LEAD_CREATED">Lead Created</option>
                <option value="PICKUP_ASSIGNED">Pickup Assigned</option>
                <option value="PICKUP_SCHEDULED">Pickup Scheduled</option>
                <option value="PQ_IN_PROGRESS">PQ In Progress</option>
                <option value="PQ_COMPLETED">PQ Completed</option>
                <option value="PRICE_NEGOTIATED">Price Negotiated</option>
                <option value="PAYMENT_COMPLETED">Payment Completed</option>
                <option value="DEVICE_COLLECTED">Device Collected</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="GRN_COMPLETED">GRN Completed</option>
              </select>
            </div>

            <div className="w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sources</option>
                {leadSources.map(source => (
                  <option key={source.source_id} value={source.source_name}>
                    {source.source_name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] flex items-center gap-2"
            >
              <Filter className="w-4 h-4" /> Filter
            </button>

            <button
              type="button"
              onClick={() => { setSearchTerm(''); setStatusFilter(''); setSourceFilter(''); fetchLeads(); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Reset
            </button>
          </form>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Device</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Quote</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2b3380]"></div>
                      </div>
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      No leads found. Leads created from the Sell Phone page will appear here.
                    </td>
                  </tr>
                ) : (
                  leads.map(lead => (
                    <tr key={lead.lead_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-blue-600">{lead.lead_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-800">{lead.customer_info?.name || '-'}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {lead.customer_info?.mobile || '-'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-800">
                            {lead.device_info?.brand} {lead.device_info?.model}
                          </p>
                          <p className="text-sm text-gray-500">{lead.device_info?.storage || lead.device_info?.variant}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          lead.source_type === 'Website' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {lead.source_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800">{formatCurrency(lead.quoted_price)}</p>
                        {lead.final_price && lead.final_price !== lead.quoted_price && (
                          <p className="text-sm text-green-600">Final: {formatCurrency(lead.final_price)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-800'}`}>
                          {getStatusIcon(lead.status)}
                          {lead.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => { setSelectedLead(lead); setShowDetailModal(true); }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Edit Lead"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead Detail Modal */}
        {showDetailModal && selectedLead && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Lead Details</h2>
                    <p className="text-sm text-gray-500">{selectedLead.lead_id}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <XCircle className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_COLORS[selectedLead.status]}`}>
                    {getStatusIcon(selectedLead.status)}
                    {selectedLead.status?.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-gray-500">
                    Created: {formatDate(selectedLead.created_at)}
                  </span>
                </div>

                {/* Customer Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Customer Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Name</p>
                      <p className="font-medium">{selectedLead.customer_info?.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Mobile</p>
                      <p className="font-medium">{selectedLead.customer_info?.mobile}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500">Address</p>
                      <p className="font-medium">
                        {selectedLead.pickup_address}, {selectedLead.pickup_city} - {selectedLead.pickup_pincode}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Device Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Brand</p>
                      <p className="font-medium">{selectedLead.device_info?.brand}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Model</p>
                      <p className="font-medium">{selectedLead.device_info?.model}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Storage/Variant</p>
                      <p className="font-medium">{selectedLead.device_info?.storage || selectedLead.device_info?.variant}</p>
                    </div>
                    {selectedLead.device_info?.imei && (
                      <div>
                        <p className="text-gray-500">IMEI</p>
                        <p className="font-mono font-medium">{selectedLead.device_info?.imei}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-gradient-to-r from-[#2b3380] to-[#4a5bc7] rounded-xl p-4 text-white">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Pricing
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-blue-200">Quoted Price</p>
                      <p className="text-2xl font-bold">{formatCurrency(selectedLead.quoted_price)}</p>
                    </div>
                    <div>
                      <p className="text-blue-200">PQ Grade</p>
                      <p className="text-2xl font-bold">
                        {selectedLead.pq_grade ? (
                          <span className={`px-2 py-1 rounded ${
                            ['Z1', 'Z2'].includes(selectedLead.pq_grade) ? 'bg-green-500' :
                            ['Z3', 'Z4'].includes(selectedLead.pq_grade) ? 'bg-blue-500' :
                            ['Z5', 'Z6'].includes(selectedLead.pq_grade) ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}>
                            {selectedLead.pq_grade} {selectedLead.pq_grade_name && `(${selectedLead.pq_grade_name})`}
                          </span>
                        ) : selectedLead.pqc_grade || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-200">Final Price</p>
                      <p className="text-2xl font-bold">{formatCurrency(selectedLead.pq_final_price || selectedLead.final_price) || '-'}</p>
                    </div>
                  </div>
                  
                  {/* PQ Pricing Details */}
                  {selectedLead.pq_price_calculation && (
                    <div className="mt-4 pt-4 border-t border-blue-400/30 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-blue-200">Deductions</p>
                        <p className="font-semibold text-red-300">-{formatCurrency(selectedLead.pq_price_calculation.total_deduction)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200">Packaging Top-up</p>
                        <p className="font-semibold text-green-300">+{formatCurrency(selectedLead.pq_price_calculation.packaging_topup)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200">Warranty Tier</p>
                        <p className="font-semibold">{selectedLead.pq_warranty_tier || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Source Info */}
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-gray-500">Source:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedLead.source_type === 'Website' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedLead.source_type} - {selectedLead.source_name}
                    </span>
                  </div>
                  {selectedLead.external_reference_id && (
                    <div>
                      <span className="text-gray-500">External Ref:</span>
                      <span className="ml-2 font-mono">{selectedLead.external_reference_id}</span>
                    </div>
                  )}
                </div>

                {/* Status History */}
                {selectedLead.status_history?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Status History</h3>
                    <div className="space-y-2">
                      {selectedLead.status_history.slice().reverse().map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm">
                          <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="font-medium">{entry.status?.replace(/_/g, ' ')}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500">{formatDate(entry.timestamp)}</span>
                          {entry.notes && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">{entry.notes}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560]">
                  Assign Pickup Engineer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BuybackLeads;
